import { useCallback } from "react";

/**
 * Drops the zeros a value cannot start with, so 023 reads as 23. A lone zero
 * stays, and so does the one in front of a decimal separator, so 0 and 0,5
 * are left as they are.
 */
const withoutLeadingZeros = (value: string) =>
  value.replace(/^(-?)0+(?=\d)/, "$1");

export const normalizeNumericString = (
  input: string,
  {
    useNegativeValues = false,
    maxDecimals,
    maxNumbers,
    stripLeadingZeros = false,
  }: {
    maxNumbers?: number;
    maxDecimals?: number;
    useNegativeValues?: boolean;
    /**
     * Only for fields holding an amount. Identifiers such as invoice or
     * booking numbers are searched for as they were typed.
     */
    stripLeadingZeros?: boolean;
  }
): string | undefined => {
  const normalized = (value: string) =>
    stripLeadingZeros ? withoutLeadingZeros(value) : value;

  if (input === "") {
    return "";
  }

  if (input === "-") {
    return useNegativeValues ? input : undefined;
  }

  const inputWithDot = input.replace(/,/g, ".");
  const isNegative = parseFloat(inputWithDot) < 0;

  if (!useNegativeValues && isNegative) {
    return undefined;
  }

  const [before, after = ""] = inputWithDot
    .slice(isNegative ? 1 : 0)
    .split(".");

  if (maxDecimals != null && after.length > maxDecimals) {
    return undefined;
  }

  if (maxNumbers != null && before.length > maxNumbers) {
    return undefined;
  }

  if (
    maxDecimals === 0 &&
    new RegExp("^-?[0-9]+([,.]?)([0-9]+)?$").test(input)
  ) {
    return normalized(input.replace(/,|\./, ""));
  }

  if (input && !new RegExp("^-?[0-9]+([,.]?)([0-9]+)?$").test(input)) {
    return undefined;
  }

  return normalized(input.replace(/\./, ","));
};

type Setter = (s: string) => void;
export const useNumericTextInputChange = (
  setValue: Setter,
  maxNumbers?: number,
  maxDecimals?: number,
  useNegativeValues?: boolean,
  stripLeadingZeros?: boolean
): Setter =>
  useCallback(
    (input: string) => {
      const newValue = normalizeNumericString(input, {
        maxDecimals,
        maxNumbers,
        useNegativeValues,
        stripLeadingZeros,
      });
      if (newValue !== undefined) {
        setValue(newValue);
      }
    },
    [setValue, maxNumbers, maxDecimals, useNegativeValues, stripLeadingZeros]
  );
